/**
 * Base Service Class
 *
 * Provides common functionality for all API service classes.
 * Handles authentication, error handling, and request/response processing.
 *
 * Architecture:
 * - All services extend this base class
 * - Provides consistent API client patterns
 * - Handles CSRF tokens and authentication headers
 * - Standardizes error handling
 */

import { supabase } from "@/integrations/supabase/client";
import { apiInterceptor } from "../api/interceptor";
import { handleError, AppError } from "@/utils/errorHandler";
import {
  clearCachedCsrfToken,
  getCachedCsrfToken,
  setCachedCsrfToken,
} from "@/utils/csrfTokenCache";
import {
  buildAppErrorFromHttpResponse,
  parseJsonErrorBody,
  shouldAttemptCsrfRetry,
} from "@/services/base/BaseServiceHelpers";

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || "https://tellerwand.com";

export interface RequestOptions extends globalThis.RequestInit {
  /** Whether to include CSRF token (default: true for state-changing operations) */
  includeCSRF?: boolean;

  /** Whether to retry on 403 errors (default: true) */
  retryOn403?: boolean;

  /** Custom error handler */
  onError?: (error: AppError) => void;
}

/**
 * Base service class for all API services
 */
export abstract class BaseService {
  protected baseUrl: string;

  constructor(servicePath: string) {
    this.baseUrl = `${API_BASE_URL}/api/${servicePath}`;
  }

  /**
   * Get CSRF token from API Gateway
   * Caches the token to avoid multiple requests
   */
  protected async getCSRFToken(): Promise<string> {
    const sharedToken = getCachedCsrfToken();
    if (sharedToken) {
      return sharedToken;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/csrf-token`, {
        method: "GET",
        credentials: "include", // Important: include cookies
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.warn(`Failed to fetch CSRF token: ${response.status}`);
        return "";
      }

      const data = await response.json();
      const token = data.csrfToken ?? "";
      if (token) {
        setCachedCsrfToken(token);
      }

      return token;
    } catch (error) {
      console.warn("Error fetching CSRF token:", error);
      // Return empty string as fallback to allow request to proceed
      return "";
    }
  }

  /**
   * Clear cached CSRF token (useful for token refresh scenarios)
   */
  protected clearCSRFToken(): void {
    clearCachedCsrfToken();
  }

  /**
   * Get authorization headers with current user token and optional CSRF token
   */
  protected async getAuthHeaders(includeCSRF: boolean = true): Promise<globalThis.HeadersInit> {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const headers: globalThis.HeadersInit = {
      "Content-Type": "application/json",
    };

    // Add CSRF token if requested
    if (includeCSRF) {
      const csrfToken = await this.getCSRFToken();
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
      }
    }

    // Add JWT token if user is authenticated
    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }

    return headers;
  }

  /**
   * Generic fetch wrapper with error handling and CSRF retry
   */
  protected async fetchWithAuth<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { includeCSRF = true, retryOn403 = true, onError, ...fetchOptions } = options;

    const headers = await this.getAuthHeaders(includeCSRF);
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await apiInterceptor.intercept(
        async () => {
          const res = await fetch(url, {
            ...fetchOptions,
            credentials: "include", // Important: include cookies for CSRF
            headers: {
              ...headers,
              ...fetchOptions.headers,
            },
          });

          // If we get 403 Forbidden, it might be an invalid CSRF token
          // Clear cached token and retry once if enabled
          if (shouldAttemptCsrfRetry(res.status, retryOn403, Boolean(getCachedCsrfToken()))) {
            console.warn("⚠️ Got 403, clearing CSRF token and retrying...");
            this.clearCSRFToken();

            const newHeaders = await this.getAuthHeaders(includeCSRF);
            const retryRes = await fetch(url, {
              ...fetchOptions,
              credentials: "include",
              headers: {
                ...newHeaders,
                ...fetchOptions.headers,
              },
            });

            if (!retryRes.ok) {
              const errorData = await parseJsonErrorBody(retryRes);
              throw buildAppErrorFromHttpResponse(errorData, retryRes.status);
            }

            return retryRes.json();
          }

          if (!res.ok) {
            const errorData = await parseJsonErrorBody(res);
            throw buildAppErrorFromHttpResponse(errorData, res.status);
          }

          return res.json();
        },
        `${this.constructor.name}_${endpoint}`,
        { trackPerformance: true }
      );

      return response;
    } catch (error) {
      const appError = handleError(error, `${this.constructor.name}.fetchWithAuth`, {
        endpoint,
        method: fetchOptions.method || "GET",
      });

      // Call custom error handler if provided
      if (onError) {
        onError(appError);
      }

      throw appError;
    }
  }

  /**
   * GET request helper
   */
  protected get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.fetchWithAuth<T>(endpoint, {
      ...options,
      method: "GET",
      includeCSRF: false, // GET requests don't need CSRF
    });
  }

  /**
   * POST request helper
   */
  protected post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.fetchWithAuth<T>(endpoint, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request helper
   */
  protected put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request helper
   */
  protected patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request helper
   */
  protected delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.fetchWithAuth<T>(endpoint, {
      ...options,
      method: "DELETE",
    });
  }
}
